import { fileUrl } from "@/lib/api";
import { useQuote } from "@/context/QuoteContext";
import { testIds } from "@/constants/testIds";
import { Plus, Check, Package2, Speaker, Sparkles, Video, Music, Wrench, Ban } from "lucide-react";

const CATEGORY_ICONS = {
  Audio: Speaker,
  Luci: Sparkles,
  Video: Video,
  "DJ & FX": Music,
  "Strutture & Truss": Wrench,
};

export default function ItemCard({ item }) {
  const { lines, addItem, availability, startDate, endDate } = useQuote();
  const inQuote = lines.find((l) => l.item_id === item.id);
  const CategoryIcon = CATEGORY_ICONS[item.category] || Package2;
  const img = fileUrl(item.image_path);

  const hasDates = !!(startDate && endDate);
  const av = availability[item.id];
  const availQty = hasDates && av ? av.available_qty : item.quantity;
  const unavailable = hasDates && av && av.available_qty === 0;
  const currentInCart = inQuote ? inQuote.quantity : 0;
  const canAddMore = availQty > currentInCart;

  return (
    <div
      data-testid={testIds.itemCard(item.id)}
      className={`group bg-zinc-900/90 border rounded-xl overflow-hidden shadow-xl transition-all duration-200 hover:-translate-y-0.5 flex flex-col ${
        unavailable
          ? "border-rose-900/70 opacity-70"
          : "border-zinc-800 hover:border-rose-900/60"
      }`}
    >
      <div className="relative aspect-[16/10] bg-gradient-to-br from-zinc-950 to-zinc-900 overflow-hidden border-b border-zinc-800">
        {img ? (
          <img
            src={img}
            alt={`Noleggio ${item.name}${item.category ? ` — ${item.category}` : ""} a Como e Milano | Spectrum Service`}
            loading="lazy"
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-zinc-700">
            <CategoryIcon size={48} strokeWidth={1.25} />
          </div>
        )}
        <span className="absolute top-2 left-2 bg-zinc-950/85 backdrop-blur border border-zinc-700/60 text-zinc-300 px-2 py-0.5 rounded text-[10px] font-mono uppercase tracking-wider flex items-center gap-1">
          <CategoryIcon size={11} /> {item.category}
        </span>
        <span
          data-testid={testIds.itemAvailability(item.id)}
          className={`absolute top-2 right-2 px-2 py-0.5 rounded-full text-[10px] font-mono font-medium border ${
            unavailable
              ? "bg-rose-500/15 text-rose-300 border-rose-500/40"
              : hasDates
                ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/25"
                : "bg-emerald-500/10 text-emerald-400 border-emerald-500/20"
          }`}
        >
          {hasDates
            ? `${availQty}/${item.quantity} disp.`
            : `${item.quantity} disp.`}
        </span>
      </div>

      <div className="p-4 flex flex-col flex-1">
        <h3 className="font-heading font-bold text-white text-base leading-tight line-clamp-2">
          {item.name}
        </h3>
        {item.description && (
          <p className="text-xs text-zinc-500 mt-1.5 line-clamp-2">
            {item.description}
          </p>
        )}
        {unavailable && (
          <div className="mt-2 text-[10px] font-mono uppercase tracking-widest text-rose-400 flex items-center gap-1">
            <Ban size={11} /> Non disponibile per queste date
          </div>
        )}
        <div className="mt-3 flex items-end justify-between gap-2 pt-3 border-t border-zinc-800/70">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
              al giorno
            </div>
            <div className="font-mono font-bold text-lg text-emerald-400">
              €{item.daily_price.toFixed(2)}
            </div>
          </div>
          <button
            data-testid={testIds.addToQuoteBtn(item.id)}
            onClick={() => addItem(item)}
            disabled={unavailable || !canAddMore}
            className={`min-h-[40px] px-3 rounded-lg font-bold text-xs uppercase tracking-wider transition-all active:scale-95 flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:scale-100 ${
              inQuote
                ? "bg-emerald-600/20 text-emerald-300 border border-emerald-500/40 hover:bg-emerald-600/30"
                : "bg-rose-600 hover:bg-rose-500 text-white shadow-lg shadow-rose-900/30"
            }`}
          >
            {unavailable ? (
              <>
                <Ban size={14} /> N/D
              </>
            ) : inQuote ? (
              <>
                <Check size={14} /> {inQuote.quantity}
              </>
            ) : (
              <>
                <Plus size={14} /> Aggiungi
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
